import { createContext, useContext, useEffect, useState } from "react";
import {
  APIProvider,
  APILoadingStatus,
  useApiLoadingStatus,
} from "@vis.gl/react-google-maps";
import { getMapsKey } from "../services/mapsService";

// The Maps browser key is served by the API at runtime (GET /settings/mapsKey)
// so it lives in api/.env rather than the committed client build. When no key
// is configured the children render without the APIProvider and map features
// hide themselves (useMapsKey() returns null).
const MapsKeyContext = createContext(null);

export const useMapsKey = () => useContext(MapsKeyContext);

// Module-level cache: fetch the key once per page load.
let cachedKey;

// Rendered inside APIProvider so it can watch the loader. When Google rejects
// the key — invalid key, HTTP referrer not allowed, billing disabled, or an
// API that isn't enabled all surface as AUTH_FAILURE — or the script fails to
// load, we flip the context key back to null. That makes AddressField fall
// back to a plain TextField and PropertyMap hide itself, instead of leaving
// Google's broken map tiles / autocomplete assets on screen.
const MapsKeyBridge = ({ apiKey, children }) => {
  const status = useApiLoadingStatus();
  const failed =
    status === APILoadingStatus.AUTH_FAILURE ||
    status === APILoadingStatus.FAILED;
  return (
    <MapsKeyContext.Provider value={failed ? null : apiKey}>
      {children}
    </MapsKeyContext.Provider>
  );
};

export const MapsProvider = ({ children }) => {
  const [key, setKey] = useState(cachedKey);

  useEffect(() => {
    if (cachedKey !== undefined) {
      setKey(cachedKey);
      return;
    }
    getMapsKey().then((fetched) => {
      cachedKey = fetched || null;
      setKey(cachedKey);
    });
  }, []);

  if (!key) {
    return (
      <MapsKeyContext.Provider value={null}>{children}</MapsKeyContext.Provider>
    );
  }

  return (
    <APIProvider
      apiKey={key}
      // Silently fall back: MapsKeyBridge flips the context to null so map and
      // autocomplete features hide themselves. A missing/misconfigured key
      // (e.g. localhost isn't an allowed referrer) is an expected condition, so
      // we don't log — the address field just behaves like a plain TextField.
      onError={() => {}}
    >
      <MapsKeyBridge apiKey={key}>{children}</MapsKeyBridge>
    </APIProvider>
  );
};
